import React, { useState } from 'react';
import { ExternalLink, Github, Filter, Info, Code2, Sparkles, Lightbulb, Zap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { usePortfolio } from '@/context/PortfolioContext';
import {
  SectionShell,
  SectionInner,
  SectionHeader,
  SectionLoading,
  SectionError,
} from '@/components/section/sectionLayout';

const Projects = () => {
  const { portfolioData, loading, error } = usePortfolio();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);

  const categories = [
    { id: 'all', label: 'Tous' },
    { id: 'fullstack', label: 'Full-Stack' },
    { id: 'frontend', label: 'Frontend' },
    { id: 'backend', label: 'Backend' },
    { id: 'devops', label: 'DevOps' },
  ];

  const projects = portfolioData?.projects || [];
  const filteredProjects =
    selectedCategory === 'all'
      ? projects
      : projects.filter((project) => project.category === selectedCategory);

  if (loading) return <SectionLoading id="projects" variant="muted" />;
  if (error) return <SectionError id="projects" error={error} variant="muted" />;

  return (
    <SectionShell id="projects" variant="muted">
      <SectionInner>
        <SectionHeader
          title="Projets"
          highlight="réalisés"
          description="Une sélection de projets qui démontrent mes compétences techniques et ma capacité à livrer des solutions de qualité"
        />

        <div className="mb-8 sm:mb-12">
          <div className="mb-3 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 sm:hidden">
            <Filter className="h-4 w-4" />
            <span>Filtres</span>
          </div>
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-2 sm:mx-0 sm:flex-wrap sm:justify-center sm:overflow-visible sm:px-0">
            {categories.map((category) => (
              <Button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                variant="outline"
                size="sm"
                className={`shrink-0 ${
                  selectedCategory === category.id
                    ? 'border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600'
                    : 'border-gray-300 text-gray-600 hover:border-emerald-500/50 hover:text-emerald-500 dark:border-[#1f2937] dark:text-gray-400'
                }`}
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>

        {filteredProjects.length === 0 ? (
          <p className="py-12 text-center text-gray-600 dark:text-gray-400">
            Aucun projet dans cette catégorie.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 xl:grid-cols-2">
            {filteredProjects.map((project) => (
              <Card
                key={project.id}
                className="group overflow-hidden border-gray-200 bg-white dark:border-[#1f2937] dark:bg-[#1a1f2e] motion-safe:transition-shadow motion-safe:hover:shadow-xl motion-safe:hover:shadow-emerald-500/10"
              >
                <div className="relative h-44 overflow-hidden bg-gray-100 dark:bg-gray-800 sm:h-48 md:h-52">
                  <img
                    src={project.image}
                    alt={project.name}
                    className="h-full w-full object-cover motion-safe:transition-transform motion-safe:group-hover:scale-105"
                    loading="lazy"
                  />
                </div>

                <CardHeader className="space-y-2">
                  <CardTitle className="text-lg text-gray-900 dark:text-white sm:text-xl">
                    {project.name}
                  </CardTitle>
                  <CardDescription className="line-clamp-3 text-sm text-gray-600 dark:text-gray-400 sm:text-base">
                    {project.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    {(project.technologies || []).map((tech, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      >
                        {tech}
                      </Badge>
                    ))}
                  </div>

                  {(project.features || []).length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Fonctionnalités clés
                      </h4>
                      <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        {project.features.slice(0, 3).map((feature, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-emerald-400">•</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full border-emerald-500/50 text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400 sm:flex-1"
                      onClick={() => setSelectedProject(project)}
                    >
                      <Info className="mr-2 h-4 w-4" />
                      Détails
                    </Button>
                    {project.githubUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full border-emerald-500/50 text-emerald-600 hover:bg-emerald-500/10 dark:text-emerald-400 sm:flex-1"
                        onClick={() => window.open(project.githubUrl, '_blank')}
                      >
                        <Github className="mr-2 h-4 w-4" />
                        Code
                      </Button>
                    )}
                    {project.liveUrl && (
                      <Button
                        size="sm"
                        className="w-full bg-emerald-500 text-white hover:bg-emerald-600 sm:flex-1"
                        onClick={() => window.open(project.liveUrl, '_blank')}
                      >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Démo
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </SectionInner>

      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto border-gray-200/50 bg-white dark:border-[#1f2937]/50 dark:bg-[#1a1f2e]">
          {selectedProject && (
            <>
              <DialogHeader className="space-y-3 pb-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedProject.name}
                    </DialogTitle>
                    <DialogDescription className="mt-2 text-base text-justify text-gray-600 dark:text-gray-400">
                      {selectedProject.description}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-8">
                {selectedProject.image && (
                  <div className="relative overflow-hidden rounded-xl shadow-lg bg-gray-100 dark:bg-gray-800">
                    <img
                      src={selectedProject.image}
                      alt={selectedProject.name}
                      className="max-h-[400px] w-full object-contain motion-safe:transition-transform motion-safe:hover:scale-105"
                    />
                  </div>
                )}

                <div className="space-y-6">
                  <div className="rounded-xl border border-gray-200/50 bg-gray-50/50 p-5 dark:border-[#1f2937]/50 dark:bg-[#242b3d]/50">
                    <div className="mb-4 flex items-center gap-2">
                      <Code2 className="h-5 w-5 text-emerald-500" />
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Stack technique
                      </h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(selectedProject.technologies || []).map((tech, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-sm font-medium text-emerald-600 dark:text-emerald-400 motion-safe:transition-all motion-safe:hover:border-emerald-500/50 motion-safe:hover:bg-emerald-500/20"
                        >
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {(selectedProject.features || []).length > 0 && (
                    <div className="rounded-xl border border-gray-200/50 bg-gray-50/50 p-5 dark:border-[#1f2937]/50 dark:bg-[#242b3d]/50">
                      <div className="mb-4 flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-emerald-500" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Fonctionnalités principales
                        </h3>
                      </div>
                      <ul className="space-y-3">
                        {selectedProject.features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-3 text-gray-600 dark:text-gray-400">
                            <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/10">
                              <Zap className="h-3 w-3 text-emerald-500" />
                            </div>
                            <span className="leading-relaxed">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(selectedProject.challenges || []).length > 0 && (
                    <div className="rounded-xl border border-gray-200/50 bg-gray-50/50 p-5 dark:border-[#1f2937]/50 dark:bg-[#242b3d]/50">
                      <div className="mb-4 flex items-center gap-2">
                        <Lightbulb className="h-5 w-5 text-emerald-500" />
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          Défis techniques & solutions
                        </h3>
                      </div>
                      <ul className="space-y-3">
                        {selectedProject.challenges.map((challenge, index) => (
                          <li key={index} className="flex items-start gap-3 text-gray-600 dark:text-gray-400">
                            <div className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/10">
                              <Lightbulb className="h-3 w-3 text-emerald-500" />
                            </div>
                            <span className="leading-relaxed">{challenge}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {(selectedProject.githubUrl || selectedProject.liveUrl) && (
                  <div className="flex flex-col gap-3 pt-4 sm:flex-row">
                    {selectedProject.githubUrl && (
                      <Button
                        variant="outline"
                        size="lg"
                        className="w-full border-emerald-500/50 text-emerald-600 hover:border-emerald-500 hover:bg-emerald-500/10 dark:text-emerald-400 sm:flex-1"
                        onClick={() => window.open(selectedProject.githubUrl, '_blank')}
                      >
                        <Github className="mr-2 h-5 w-5" />
                        Voir le code source
                      </Button>
                    )}
                    {selectedProject.liveUrl && (
                      <Button
                        size="lg"
                        className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-500/25 hover:from-emerald-600 hover:to-emerald-700 sm:flex-1"
                        onClick={() => window.open(selectedProject.liveUrl, '_blank')}
                      >
                        <ExternalLink className="mr-2 h-5 w-5" />
                        Voir la démo en ligne
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </SectionShell>
  );
};

export default Projects;
